import { Tv } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { zodResolver } from "@hookform/resolvers/zod";

import { useGetAvailableRoomSystemsForCall } from "../api/get-available-room-systems-for-call";
import { useSummonRoomSystem } from "../api/summon-room-system";
import {
  SummonRoomSystemFormData,
  summonRoomSystemSchema,
} from "../types/summon-room-system.schema";

let toastId: string | number;
export function SummonRoomSystemForm() {
  const sessionId = useVidyoStore((state) => state.sessionId);

  const { data: roomSystems, isSuccess: isRoomSystemsSuccess } =
    useGetAvailableRoomSystemsForCall(sessionId);

  const form = useForm<SummonRoomSystemFormData>({
    resolver: zodResolver(summonRoomSystemSchema),
    defaultValues: {
      roomSystem: "",
    },
  });

  const { mutateAsync: summonRoomSystem, isPending: isSummoningRoomSystem } =
    useSummonRoomSystem({
      onMutate: () => {
        toastId = toast.loading("Inviting room system...");
      },
      onSuccess: () => {
        toast.success("Room system has been invited!", {
          id: toastId,
        });
      },
      onError: (error) => {
        toast.error(error.message, {
          id: toastId,
        });
      },
      onSettled: () => {
        form.reset({ roomSystem: "" });
      },
    });

  const handleRoomSystemChange = async (value: string) => {
    // Don't process placeholder value
    if (value === "no-room-systems") {
      return;
    }

    await summonRoomSystem({
      sessionId,
      roomSystemId: value,
    });
  };

  return (
    <Form {...form}>
      <FormField
        control={form.control}
        name="roomSystem"
        render={({ field }) => (
          <FormItem>
            <Select
              onValueChange={(value) => {
                field.onChange(value);
                handleRoomSystemChange(value);
              }}
              value={field.value}
              disabled={!isRoomSystemsSuccess || isSummoningRoomSystem}
            >
              <FormControl>
                <SelectTrigger>
                  <Tv className="mr-2 size-4 flex-shrink-0 rotate-180" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {roomSystems && roomSystems.length > 0 ? (
                  roomSystems.map((roomSystem) => (
                    <SelectItem key={roomSystem.id} value={roomSystem.id}>
                      {roomSystem.displayName}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-room-systems" disabled>
                    No room systems available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </Form>
  );
}
